import { useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { useQuickView } from './useQuickView';
import getGridBounds from '../utils/getGridBounds';

/**
 * Canvas ref, grid bounds, quick view, initial transform, and derived animation state.
 */
export function useFlowchartCanvas(props) {
  const {
    pan,
    zoom,
    isFocusMode,
    focusContentBounds,
    contentBounds,
    allSprints,
    onSelectSprint,
    tasks,
    nestedSprints,
    isAnimatingIntoFocus = false,
    isAnimatingOutOfFocus = false,
  } = props;

  const canvasRef = useRef(null);

  const gridBounds = useMemo(
    () => getGridBounds({ isFocusMode, focusContentBounds, contentBounds }),
    [isFocusMode, focusContentBounds, contentBounds],
  );

  const onNavigateToParentMission = useCallback(
    ({ parentSprintId, parentMissionId }) => {
      const parentSprint = allSprints?.find((s) => s.id === parentSprintId);
      if (parentSprint && onSelectSprint) {
        onSelectSprint(parentSprint, { view: 'flow', missionId: parentMissionId });
      }
    },
    [allSprints, onSelectSprint],
  );

  const quickView = useQuickView({ zoom, tasks, nestedSprints, canvasRef });

  // Set initial transform on mount (before any direct DOM manipulation happens)
  useLayoutEffect(() => {
    if (canvasRef.current && !canvasRef.current.style.transform) {
      canvasRef.current.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
      canvasRef.current.style.setProperty('--inverse-zoom', 1 / zoom);
    }
  }, []); // Only on mount

  const isFocusAnimating = isAnimatingIntoFocus || isAnimatingOutOfFocus;

  return {
    canvasRef,
    gridBounds,
    quickView,
    onNavigateToParentMission,
    isFocusAnimating,
  };
}

export default useFlowchartCanvas;
